import { Copy, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type { Domain } from '@/types/api';

interface Props {
  domain: Domain;
  onRecheck: () => void;
  recheckPending: boolean;
}

type DnsStatus = string | null | undefined;

function dnsTone(status: DnsStatus): 'green' | 'red' | 'gray' {
  if (status === 'ok') return 'green';
  if (status === 'failed') return 'red';
  return 'gray';
}

function DnsRow({ label, status, hint }: { label: string; status: DnsStatus; hint: string }) {
  function copy() {
    navigator.clipboard.writeText(hint).then(() => toast.success('Copié'));
  }

  return (
    <div className="flex items-center justify-between py-2 border-b border-brand-border last:border-0">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium w-16">{label}</span>
        <Badge tone={dnsTone(status)}>{status ?? 'pending'}</Badge>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 font-mono max-w-48 truncate">{hint}</span>
        <button
          type="button"
          onClick={copy}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Copier"
        >
          <Copy size={13} />
        </button>
      </div>
    </div>
  );
}

export function DnsRecordsCard({ domain, onRecheck, recheckPending }: Props) {
  return (
    <div className="rounded-lg border border-brand-border bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-brand-dark">DNS Records</h4>
        <Button size="sm" variant="ghost" onClick={onRecheck} loading={recheckPending}>
          <RefreshCw size={12} />
          Recheck
        </Button>
      </div>
      <DnsRow
        label="SPF"
        status={domain.spf_status}
        hint={`v=spf1 include:_spf.${domain.domain} ~all`}
      />
      <DnsRow
        label="DKIM"
        status={domain.dkim_status}
        hint={`default._domainkey.${domain.domain}`}
      />
      <DnsRow
        label="DMARC"
        status={domain.dmarc_status}
        hint={`_dmarc.${domain.domain} → v=DMARC1; p=quarantine`}
      />
    </div>
  );
}
